import React, { useState } from 'react';
import Collapse from '@material-ui/core/Collapse';
import styled from 'styled-components';
import { Feature } from '@99designs/apps/spa/common/Product/ProductCard/ProductCard';
import { Box, SectionHeading, Stack } from '@99designs/emu';
import { mediaQuery } from '@99designs/emu/css';
import { borders, spaces, typography } from '@99designs/emu/settings';
import { IconDown1, IconUp1 } from '@99designs/icons';
import { FeaturesList } from '@99designs/spa-common';
import { Desktop, Mobile } from '@99designs/spa-common';
import { ProductPrice, ProductPriceProps } from '@99designs/spa-common';

const CardContainer = styled.div`
    padding: ${spaces['2bu']};
    border: ${borders.content};
    border-radius: ${borders.borderRadiuses.medium}px;
`;

const StyledHeading = styled.div`
    font-weight: ${typography.fontWeights.medium};
    font-size: ${typography.typeScale.size4}px;
    font-family: ${typography.fontFamilies.heading};
    margin-bottom: 0;
    display: inline;

    ${mediaQuery.only('small')} {
        font-size: ${typography.typeScale.size4}px;
    }
`;

const FeaturesListContainer = styled.div`
    ${mediaQuery.minimum('normal')} {
        padding-bottom: ${spaces['1.5bu']};
    }
`;

const FeaturesToggle = styled.div`
    cursor: pointer;
    display: inline;
`;

const Description = styled.p`
    margin: 0;
    padding-right: ${spaces['1.5bu']};

    ${mediaQuery.between('small', 'medium')} {
        padding-right: 0;
    }
`;

const Logo = styled.div`
    height: 46px;

    ${mediaQuery.only('small')} {
        height: 35px;
    }

    svg {
        height: 100%;
    }
`;

const PriceContainer = styled.div`
    font-weight: ${typography.fontWeights.medium};
    font-size: ${typography.typeScale.size5}px;
    font-family: ${typography.fontFamilies.heading};
    width: 250px;

    ${mediaQuery.minimum('normal')} {
        text-align: right;
    }

    ${mediaQuery.between('small', 'medium')} {
        padding-top: ${spaces['1.5bu']};
        font-size: ${typography.typeScale.size4}px;
        width: 100%;
    }
`;

interface Props extends ProductPriceProps {
    description: string;
    cta: React.ReactNode;
    title?: string;
    logo?: string;
    features?: Feature[];
}

export default function SecondaryProductCard({
    title,
    description,
    logo,
    cta,
    fromPrice,
    toPrice,
    showFixedPrice,
    features = [],
}: Props) {
    const [open, setOpen] = useState(false);

    return (
        <CardContainer data-testid="secondary-product-card">
            {logo && (
                <Box marginBottom="1.5bu">
                    <Logo
                        data-testid="secondary-product-card-logo"
                        dangerouslySetInnerHTML={{
                            __html: logo,
                        }}
                    />
                </Box>
            )}
            <Box display="flex">
                <div>
                    <Box marginBottom="1bu">
                        {title && <SectionHeading>{title}</SectionHeading>}
                    </Box>
                    <Mobile>
                        <Box marginBottom="1bu">
                            <StyledHeading>
                                {fromPrice && (
                                    <ProductPrice
                                        fromPrice={fromPrice}
                                        toPrice={toPrice}
                                        showFixedPrice={showFixedPrice}
                                    />
                                )}
                            </StyledHeading>
                        </Box>
                    </Mobile>
                    <Stack space="1bu">
                        <Description>{description}</Description>
                        {features.length > 0 && (
                            <div>
                                <Collapse in={open} timeout="auto" unmountOnExit>
                                    <FeaturesListContainer>
                                        <FeaturesList
                                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                            features={features.map((feature: any) => {
                                                return {
                                                    isAvailable: true,
                                                    title: feature.title,
                                                };
                                            })}
                                            aria-hidden={!open}
                                        />
                                    </FeaturesListContainer>
                                </Collapse>
                                <FeaturesToggle
                                    onClick={() => setOpen((open) => !open)}
                                    aria-expanded={open}
                                    data-testid="secondary-product-card-feature-toggle"
                                >
                                    <StyledHeading>
                                        {!open ? __('View features') : __('Hide features')}
                                        <Box paddingLeft="0.25bu" display="inline">
                                            {open ? <IconUp1 /> : <IconDown1 />}
                                        </Box>
                                    </StyledHeading>
                                </FeaturesToggle>
                            </div>
                        )}
                    </Stack>
                </div>
                <Desktop>
                    <Box
                        display="flex"
                        flexDirection="column"
                        alignItems="flex-end"
                        justifyContent="space-between"
                        height="100%"
                    >
                        <StyledHeading>
                            <PriceContainer>
                                {fromPrice && (
                                    <ProductPrice
                                        fromPrice={fromPrice}
                                        toPrice={toPrice}
                                        showFixedPrice={showFixedPrice}
                                    />
                                )}
                            </PriceContainer>
                        </StyledHeading>
                        <Box width={'200px'} data-testid="secondary-product-card-desktop-cta">
                            {cta}
                        </Box>
                    </Box>
                </Desktop>
            </Box>
            <Mobile>
                <Box marginTop="1.5bu">{cta}</Box>
            </Mobile>
        </CardContainer>
    );
}
